import React, { useState } from 'react';

import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';

import ViewDocumentDrawer from './ViewDocumentDrawer';
import { useUserData } from '../../../App/Component/UserDataProvider';
import { download_file } from '../../../Draft/Component/Helper';
import { useResponsive } from '../../../RiverusUI/Components/ResponsiveProvider';
import ReusableConfirmationModal from '../../../RiverusUI/Components/ReusableConfirmationModal';
import ListSkeleton from '../../../RiverusUI/Components/Skeleton/ListSkeleton';
import {
  bulkDeleteDocs,
  fetchDownloadUrl,
} from '../../../Services/DocumentLibrary';
import { renewAmendChipValue, submissionTypes } from '../DocLibStaticData';
import { handleOpenContract } from './helper';
import ShowPopper from '../../../RiverusUI/Components/ShowPopper';

interface Props {
  open: boolean;
  onClose: VoidFunction;
  contractLoading: boolean;
  userCanDoAction: boolean;
  masterContract: any[];
  amendmentContract: any[];
  supportContract: any[];
}

const ConnectedDocsDrawer: React.FC<Props> = ({
  open,
  onClose,
  contractLoading,
  userCanDoAction,
  masterContract,
  amendmentContract,
  supportContract,
}) => {
  const { isMobile } = useResponsive();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { userIsViewer } = useUserData();

  const [openViewDrawer, setOpenViewDrawer] = useState<boolean>(false);
  const [viewDocumentId, setViewDocumentId] = useState<string>('');
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [deleteDocId, setDeleteDocId] = useState<string>('');

  const handleCloseViewDrawer = () => {
    setViewDocumentId('');
    setOpenViewDrawer(false);
  };

  const handleOpenDeleteModal = (id: string) => {
    if (userCanDoAction) {
      setOpenDeleteModal(true);
      setDeleteDocId(id);
    } else {
      enqueueSnackbar(
        'You are not the owner of the contract. Owner or Admin can perform this action',
        {
          variant: 'info',
        }
      );
    }
  };

  const handleCloseDeleteModal = () => {
    setDeleteDocId('');
    setOpenDeleteModal(false);
  };

  const { mutate: deleteUploadedDoc } = useMutation({
    mutationKey: ['delete_connected_doc'],
    mutationFn: bulkDeleteDocs,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['get_contract_data_id'] });
      queryClient.invalidateQueries({ queryKey: ['all-connected-docs'] });
      enqueueSnackbar('Document deleted successfully!', {
        variant: 'success',
      });
      handleCloseDeleteModal();
    },
    onError: () => {
      enqueueSnackbar('Failed to delete Document!', {
        variant: 'error',
      });
    },
  });

  const { mutate: downloadFile } = useMutation({
    mutationFn: fetchDownloadUrl,
    onSuccess: (response: any) => {
      download_file(response);
    },
  });

  const handleDownloadFile = (id: string) => {
    downloadFile(id);
  };

  const handleViewContractFile = (id: string) => {
    setOpenViewDrawer(true);
    setViewDocumentId(id);
  };

  const canAccessContract = (contractId: string) => {
    if (userCanDoAction) {
      handleOpenContract(contractId?.toString());
    } else {
      enqueueSnackbar('You are not assigned any role for this contract!', {
        variant: 'info',
      });
    }
  };

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        sx={{
          '& .MuiDrawer-paper': {
            width: '600px',
          },
        }}
        className={isMobile ? 'drawer-wrapper' : ''}
      >
        {contractLoading ? (
          <Box px={2}>
            <ListSkeleton />
          </Box>
        ) : (
          <Stack spacing={2} width={isMobile ? 'auto' : '570px'} marginX="15px">
            <Stack
              direction="row"
              spacing={1}
              paddingTop="10px"
              alignItems="center"
            >
              <IconButton onClick={onClose}>
                <CloseIcon sx={{ color: '#88305F' }} />
              </IconButton>
              <Typography>Connected Documents </Typography>
            </Stack>
            <Stack direction="column" spacing={2} marginY={2} width="100%">
              <Typography fontSize="15px" fontWeight={600}>
                Original documents
              </Typography>

              {masterContract?.length > 0 &&
                masterContract?.map((item: any) => (
                  <Stack
                    key={item?.id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                    flexWrap={isMobile ? 'wrap' : 'nowrap'}
                    gap={1}
                  >
                    <Typography>
                      <span
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (!item?.deleted_status)
                            canAccessContract(item?.id);
                        }}
                      >
                        <ShowPopper title={item?.title} />
                      </span>
                    </Typography>
                    <Stack direction="row" spacing={1}>
                      <Chip label="Master" sx={{ background: '#88305F24' }} />
                      {renewAmendChipValue(item?.docID) && (
                        <Chip
                          label={renewAmendChipValue(item?.docID)}
                          sx={{ background: '#88305F24' }}
                        />
                      )}
                      {item?.deleted_status && <Chip label="Delete" />}
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => handleDownloadFile(item?.id)}
                        disabled={item?.deleted_status || userIsViewer}
                      >
                        <SaveAltIcon />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => handleOpenDeleteModal(item?.id)}
                        disabled={item?.deleted_status || userIsViewer}
                      >
                        <DeleteOutlineIcon />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0 }}
                        onClick={() => {
                          handleViewContractFile(item?.id);
                        }}
                        disabled={item?.deleted_status}
                      >
                        <ArrowRightIcon />
                      </IconButton>
                    </Stack>
                  </Stack>
                ))}

              {amendmentContract?.length > 0 && (
                <Stack direction="column" spacing={1}>
                  <Typography fontSize="15px" fontWeight={600}>
                    Amendment documents
                  </Typography>
                  {amendmentContract?.map((item: any) => (
                    <Stack
                      key={item?.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      flexWrap={isMobile ? 'wrap' : 'nowrap'}
                      gap={1}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography>
                          <span
                            style={{ cursor: 'pointer' }}
                            onClick={() => {
                              if (!item?.deleted_status)
                                canAccessContract(item?.id);
                            }}
                          >
                            <ShowPopper title={item?.title} />
                          </span>
                        </Typography>
                        {item?.terminate_status && <Chip label="Terminated" />}
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        {item?.submission_type && (
                          <Chip
                            sx={{ background: '#88305F24' }}
                            label={submissionTypes?.[item?.submission_type]}
                          />
                        )}
                        {renewAmendChipValue(item?.docID) && (
                          <Chip
                            label={renewAmendChipValue(item?.docID)}
                            sx={{ background: '#88305F24' }}
                          />
                        )}
                        {item?.deleted_status && <Chip label="Delete" />}
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => handleDownloadFile(item?.id)}
                          disabled={item?.deleted_status || userIsViewer}
                        >
                          <SaveAltIcon />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => handleOpenDeleteModal(item?.id)}
                          disabled={item?.deleted_status || userIsViewer}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => {
                            handleViewContractFile(item?.id);
                          }}
                          disabled={item?.deleted_status}
                        >
                          <ArrowRightIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}

              {supportContract?.length > 0 && (
                <Stack direction="column" spacing={1} pt="15px">
                  <Typography fontSize="15px" fontWeight={600}>
                    Support documents
                  </Typography>
                  {supportContract?.map((item: any) => (
                    <Stack
                      key={item?.id}
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                      flexWrap={isMobile ? 'wrap' : 'nowrap'}
                      gap={1}
                    >
                      <ShowPopper title={item?.title} />
                      <Stack direction="row" spacing={1}>
                        {item?.deleted_status && <Chip label="Delete" />}
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => handleDownloadFile(item?.id)}
                          disabled={item?.deleted_status || userIsViewer}
                        >
                          <SaveAltIcon />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => handleOpenDeleteModal(item?.id)}
                          disabled={item?.deleted_status || userIsViewer}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                        <IconButton
                          sx={{ padding: 0 }}
                          onClick={() => {
                            handleViewContractFile(item?.id);
                          }}
                          disabled={item?.deleted_status}
                        >
                          <ArrowRightIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Stack>
        )}
      </Drawer>
      {openViewDrawer && (
        <ViewDocumentDrawer
          open={openViewDrawer}
          onClose={handleCloseViewDrawer}
          documentId={viewDocumentId}
        />
      )}
      {openDeleteModal && (
        <ReusableConfirmationModal
          open={openDeleteModal}
          onClose={handleCloseDeleteModal}
          title="Delete Document"
          cancelBtnText="No, Go Back"
          confirmBtnText="Yes, Delete"
          onConfirm={() => deleteUploadedDoc([deleteDocId])}
        >
          <Stack spacing={2}>
            <Typography>Are you sure?</Typography>
            <Typography>
              The selected document will be removed permanently.
            </Typography>
            <Typography>Note: This action is not reversible.</Typography>
          </Stack>
        </ReusableConfirmationModal>
      )}
    </>
  );
};

export default ConnectedDocsDrawer;
